import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import ComponentTable from '../../../Components/Table/Index';
import { Column } from '../../../Components/Table/Types';
import * as Icon from '@mui/icons-material/';
import { useNavigate } from 'react-router-dom';
import { Form, Formik, Field } from 'formik';
import { ProdutoAtualizar, ProdutoCadastrar } from '../../../../routes/RoutesNames';
import { IconButton } from '@mui/material';
import ProdutoController from '../../../../controllers/ProdutoController';
import { Button } from '../../../../shared/components/button/Button';
import { Loader } from '../../../../shared/components/Loader';
import { HeaderComponent } from '../../../../shared/components/HeaderComponent';
import { toast } from 'react-toastify';
import { ButtonMenu } from '../../../../shared/components/ButtonMenu';
import { usePermissao } from '../../../../context/permissao/PermissaoProvider';

interface Row {
  BtnEditar?: JSX.Element;
  BtnExcluir?: JSX.Element;
  prod_id: number;
  prodID_promatec: string;
  prod_descricao: string;
  prod_codigo_barras: string;
  prod_tipo: string;
  prod_partNumber: string;
  preco_venda: number;
}

/**
 * PILOTO da permissão por ação (04/11/2026).
 *
 * Esta é a primeira tela a desabilitar botão pelo papel do usuário. ISSO NÃO É
 * PROTEÇÃO: quem protege é a PROC_ACESSO_ROTA_VERIFICAR, no servidor, que responde
 * 403 mesmo que alguém chame a rota por fora. O botão apagado existe para a pessoa
 * não tentar — e para ela descobrir o limite ANTES de preencher um cadastro inteiro.
 *
 * `visualizar` ENTRA, e é o duplo clique: ele abre o cadastro em leitura, e o
 * cadastro lê /api/produto/listarUNICO. Essa rota era livre sem querer — ela é
 * prefixada por /api/produto/listar, a entrada que o 184 criou para o combo do
 * Pedido. O 263 marcou o detalhe como `nao_livre`, então agora ele exige a tela e a
 * ação, e recusar visualizar passou a significar alguma coisa.
 *
 * A LISTAGEM continua livre, e isso é de propósito: o combo do Pedido depende dela.
 * Quem não pode visualizar continua vendo a grade, e só não abre o detalhe.
 */
export default function Produto() {
  const ref = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const tableColumns: Column[] = [
    { id: 'Id', label: 'Codigo', minWidth: 0 },
    { id: 'Descricao', label: 'Descrição', minWidth: 0 },
    // { id: 'CodigoBarras', label: 'Codigo Barras', minWidth: 0 },
    // { id: 'Tipo', label: 'Tipo', minWidth: 0 },
    { id: 'PartNumber', label: 'Part Number', minWidth: 0 },
    { id: 'ValorVenda', label: 'Valor Venda', minWidth: 0, align: 'center' },
    { id: 'ProdId', label: 'ProdId', minWidth: 0, align: 'center', hidden: true },
    { id: 'BtnMenu', label: '', width: 60, minWidth: 0, align: 'right' },
  ];

  const [tableRows, setTableRows] = useState<Row[]>([]);

  useEffect(() => {
    ref?.current?.click();
  }, []);

  const { podeFazer } = usePermissao();
  const podeIncluir = podeFazer('produto', 'incluir');
  const podeEditar = podeFazer('produto', 'editar');
  const podeExcluir = podeFazer('produto', 'excluir');
  // O duplo clique na linha. Ver o comentário do topo: a rota do detalhe deixou de ser
  // livre no 263, então aqui o botão apagado e o 403 do servidor dizem a mesma coisa.
  const podeVisualizar = podeFazer('produto', 'visualizar');

  const handleNavigation = async (): Promise<void> => {
    navigate(ProdutoCadastrar);
  };

  const handleEditItem = (item: Row, duploClick = false, duploClickId = 0) => {
    if (duploClick) {
      navigate(ProdutoAtualizar.replace(':id', duploClickId.toString()), { state: { duploClick: true } });
    }
    else {
      if (item.prod_id) {
        navigate(ProdutoAtualizar.replace(':id', item.prod_id.toString()));
      }
    }
  };

  const handleDeleteItem = async (prod_id: number) => {
    const request: any = { ProdutoId: prod_id };
    setLoading(true);
    const response = await ProdutoController.Excluir(request);
    setLoading(false);
    if (response.StatusCode === 204) {
      toast('Produto excluído com sucesso!', {
        type: 'success',
      });
    } else {
      toast('Erro ao excluir o produto!', {
        type: 'error',
      });
    }
  };

  const handleSubmit = async (values: any) => {
    const DataRows: any = [];
    setLoading(true);
    const Response = await ProdutoController.Listar(values);
    setLoading(false);
    if (Response.StatusCode === 200 && Response?.Data?.length > 0) {
      Response.Data.map((item: Row) => {
        DataRows.push({
          Id: item.prodID_promatec,
          Descricao: item.prod_descricao,
          CodigoBarras: item.prod_codigo_barras,
          Tipo: item.prod_tipo,
          PartNumber: item.prod_partNumber,
          ValorVenda: Number(item.preco_venda).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          ProdId: item.prod_id,
           BtnMenu: (
            <ButtonMenu
              items={[
                { onClick: () => handleEditItem(item), menu: 'Editar', disabled: !podeEditar },
                {
                  onClick: () => handleDeleteItem(item.prod_id),
                  menu: 'Excluir', disabled: !podeExcluir,
                  confirmar: `Excluir o produto "${item.prod_descricao ?? ''}"? Esta ação não pode ser desfeita.`,
                },
              ]}
            />
          ),
        });

        setTableRows(DataRows);
      });
    } else if (Response.StatusCode === 200 && Response?.Data?.length === 0) {
      toast('Não há registros cadastrados.', {
        type: 'warning',
      });
    } else {
      toast(Response?.Data, {
        type: 'error',
      });
    }
  };

  const initialValues = {
    ProdutoId_Promatec: '',
    CodigoBarras: '',
    PartNumber: '',
    Descricao: '',
  };

  useEffect(() => {
    handleSubmit(initialValues);
  }, []);

  return (
    <Loader loading={loading}>
      <Box
        sx={{
          '& .MuiTextField-root': { m: 1 },
        }}
      >
        <div className={'pb-2'}>
          <HeaderComponent
            className={'mb-6'}
            text={'Listagem de Produtos'}
            ButtonRight={
              <Button
                className={''}
                variant='success'
                onClick={handleNavigation}
                disabled={!podeIncluir}
                iconStart={<Icon.Add />}
                title={'Incluir'}
              />
            }
          />
          <Box mb={2} />

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
          >
            {({ errors }) => (
              <Form className={'grid lg:grid-cols-12 grid-cols-6 gap-2'}>
                <Field
                  className={'col-span-2'}
                  name='ProdutoId_Promatec'
                  as={TextField}
                  id='outlined-password-input'
                  label='Código'
                  type='text'
                  autoComplete='current-password'
                  error={Boolean(errors?.CodigoBarras)}
                  helperText={errors?.CodigoBarras}
                />

                <Field
                  className={'col-span-3'}
                  name='Descricao'
                  as={TextField}
                  label='Descrição'
                  type='text'
                  error={Boolean(errors?.Descricao)}
                  helperText={errors?.Descricao}
                />

                {/* <Field
                  className={'col-span-2'}
                  name='CodigoBarras'
                  as={TextField}
                  label='Codigo de Barras'
                  type='text'
                  error={Boolean(errors?.CodigoBarras)}
                  helperText={errors?.CodigoBarras}
                /> */}
                <Field
                  className={'col-span-3'}
                  name='PartNumber'
                  as={TextField}
                  label='PartNumber'
                  type='text'
                  error={Boolean(errors?.PartNumber)}
                  helperText={errors?.PartNumber}
                />
                <Box className={'pt-2'}>
                  <IconButton size={'large'} type={'submit'}>
                    <Icon.Search style={{ fontSize: '28px' }} />
                  </IconButton>
                </Box>
              </Form>
            )}
          </Formik>
        </div>
        <ComponentTable columns={tableColumns} rows={tableRows} onRowDoubleClick={(row) => {
          if (!podeVisualizar) {
            // Avisar aqui é mais honesto do que abrir a tela e deixá-la vazia com 403.
            toast.info('Seu perfil não permite abrir o cadastro do produto.', { type: 'warning' });
            return;
          }
          handleEditItem(row, true, row.ProdId);
        }} />
      </Box>
    </Loader>
  );
}
